import React from "react";
import PropTypes from "prop-types";

// Carries prior answers onto the current step so the player never has to hold them in memory
// (§ memory-load): each analysis question that references earlier work ("these issues", "this
// observation", "this reflection") renders the referenced answers in a quiet inset panel above
// the question. An entry is either a single `value` (text) or a `list` of strings (the issues);
// it may additionally carry `cards` - compact +/- chips naming the cards the answer was based
// on (the observation's chosen pair). Entries whose value is empty are dropped, so the panel
// only shows what actually exists.
const hasContent = ({ value, list }) => (
  (typeof value === "string" && value.trim().length > 0)
  || (Array.isArray(list) && list.some((v) => v && v.trim().length > 0))
);

// The chosen negative/positive pair as recall chips for the observation entry. Either card may
// be absent (defensive on partial session payloads), so it filters.
export const chosenPairCards = (session) => [
  { polarity: "negative", card: session.chosenNegativeCard },
  { polarity: "positive", card: session.chosenPositiveCard },
].filter(({ card }) => card).map(({ polarity, card }) => ({ polarity, title: card.title }));

const AnalysisRecall = ({ entries }) => {
  const shown = entries.filter(hasContent);
  if (shown.length === 0) return null;

  return (
    <aside className="topik-analysis__recall" aria-label={shown[0].caption || undefined}>
      {shown.map(({ key, label, value, list, cards }) => (
        <div key={key} className="topik-analysis__recall-entry">
          <span className="topik-analysis__recall-label">{label}</span>
          {cards && cards.length > 0 && (
            <div className="topik-analysis__recall-cards">
              {cards.map(({ polarity, title }) => (
                <span
                  key={polarity}
                  className={`topik-analysis__recall-card topik-analysis__recall-card--${polarity}`}
                >
                  <span aria-hidden="true">{polarity === "negative" ? "−" : "+"}</span>
                  {" "}
                  {title}
                </span>
              ))}
            </div>
          )}
          {list ? (
            <ul className="topik-analysis__recall-list">
              {list.filter((v) => v && v.trim().length > 0).map((v) => (
                <li key={v} className="topik-analysis__recall-value">{v}</li>
              ))}
            </ul>
          ) : (
            <p className="topik-analysis__recall-value">{value}</p>
          )}
        </div>
      ))}
    </aside>
  );
};

AnalysisRecall.propTypes = {
  entries: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
    list: PropTypes.arrayOf(PropTypes.string),
    cards: PropTypes.arrayOf(PropTypes.shape({
      polarity: PropTypes.oneOf(["negative", "positive"]).isRequired,
      title: PropTypes.string.isRequired,
    })),
    caption: PropTypes.string,
  })).isRequired,
};

export default AnalysisRecall;
